import { useCallback, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useToast } from '@aimsos/ui';

import { useHitlDecision } from './useHitlDecision.js';
import { HitlDecisionJsonError } from '../utils/hitlDecision.js';
import { classifyHitlResolveError } from '../services/hitlTasks.js';
import { markHitlContractDismissed } from '../utils/dismissedHitlContracts.js';

// Toast copy per resolve-error kind that dismisses the card instead of
// keeping it open for a retry (mirrors the inbox slide-out's handling).
const DISMISS_COPY = {
  already_resolved: ['chat.hitl.alreadyResolved', 'chat.hitl.alreadyResolvedDetail'],
  resume_conflict: ['chat.hitl.resumeConflict', 'chat.hitl.resumeConflictDetail'],
};

/**
 * Decision + dismissal core shared by every HITL gate-card body.
 *
 * Builds on the headless {@link useHitlDecision} (resolve + `resolving`) and
 * adds the card-level UX the bodies share: success/rejected/error toasts,
 * dismissed-contract bookkeeping, the resume-start signal, and the
 * task-shaped envelope the decision builders read. Mode-specific state
 * (comments, selections, attachments) stays in the body components — they
 * close over it in the thunk they hand to {@link run}.
 *
 * @param {{
 *   apiClient: import('axios').AxiosInstance,
 *   card: { interactionId: string, gate?: string },
 *   onResolved?: () => void,
 *   onResumeStart?: (() => void)|null,
 *   onResumeEnd?: (() => void)|null,
 *   translationNamespace?: string,
 * }} params
 */
export const useHitlGateDecision = ({
  apiClient,
  card,
  onResolved,
  onResumeStart = null,
  onResumeEnd = null,
  translationNamespace = 'chat',
}) => {
  const { t } = useTranslation(translationNamespace);
  const toast = useToast();
  const { resolving, approve, reject, submit } = useHitlDecision({ apiClient, onResolved });

  // The decision builders read `task.contractId` and `task.resumeMatch.gate` —
  // feed them a minimal task-shaped object built from the normalized card.
  const task = useMemo(
    () => ({ contractId: card.interactionId, resumeMatch: { gate: card.gate } }),
    [card.interactionId, card.gate],
  );

  /**
   * Run one decision thunk with the shared success / error UX.
   *
   * @param {() => Promise<object>} action  Zero-arg thunk (the body closes
   *   over its own mode state) resolving to the endpoint result.
   * @param {string} successTitleKey
   * @param {string} successDetailKey
   */
  const run = useCallback(
    async (action, successTitleKey, successDetailKey) => {
      // Optimistic, BEFORE the resolve round-trip: the human just decided, so
      // show the working indicator while the engine resumes. For LangGraph
      // gates the resolve call is synchronous with the WHOLE resumed run, so
      // the run's own terminal events (arriving during this await) clear the
      // indicator — calling this after the await wedged "Working…" forever,
      // since by then every clearing event had already come and gone
      // (observed live 2026-09-18). For Temporal gates the resolve returns
      // early and the next WS event clears/replaces it, exactly as before.
      onResumeStart?.();
      try {
        const result = await action();
        // Resolved for real — never let the durable poll re-seed this contract.
        markHitlContractDismissed(task.contractId);
        // A rejected decision is a SUCCESS carrying error_code 'hitl_rejected'.
        const rejected = result?.error_code === 'hitl_rejected';
        toast.success({
          title: t(rejected ? 'chat.hitl.rejected' : successTitleKey),
          message: t(rejected ? 'chat.hitl.rejectedDetail' : successDetailKey),
        });
      } catch (err) {
        if (err instanceof HitlDecisionJsonError) {
          // Malformed JSON never reached the network, so nothing resumed —
          // stop the optimistic indicator. (no JSON editor on the card paths)
          onResumeEnd?.();
          return;
        }
        const { kind } = classifyHitlResolveError(err);
        if (DISMISS_COPY[kind]) {
          // already_resolved: someone else decided; resume_conflict: the
          // Temporal update lost a race and the workflow already continued.
          // Either way the run ALREADY CONTINUED — for LangGraph the resumed
          // run's own events updated the indicator during the await. So do NOT
          // call onResumeEnd here: that would wipe a live working state and
          // unlock the composer while the agent is still running (observed live
          // 2026-09-18). The card is stale: dismiss first (and deny re-seed) so
          // a throwing toast can never strand it, then inform.
          const [titleKey, messageKey] = DISMISS_COPY[kind];
          markHitlContractDismissed(task.contractId);
          onResolved?.();
          toast.info({ title: t(titleKey), message: t(messageKey) });
          return;
        }
        // Retryable (network / 5xx): nothing resumed, so no event will ever
        // clear the optimistic indicator — stop it here and keep the card so
        // the human can retry.
        onResumeEnd?.();
        toast.error({
          title: t('chat.hitl.failed'),
          message: err?.response?.data?.detail || err?.message || String(err),
        });
      }
    },
    [task, onResolved, onResumeStart, onResumeEnd, toast, t],
  );

  // Manual dismiss (✕): the user judged this card stale (e.g. a zombie from a
  // finished run). Deny re-seed for good — persisted, so it survives a reload —
  // and clear the slot. The durable inbox still holds the task if it was real.
  const dismiss = useCallback(() => {
    markHitlContractDismissed(task.contractId);
    onResolved?.();
  }, [task, onResolved]);

  return useMemo(
    () => ({ resolving, approve, reject, submit, run, dismiss, task }),
    [resolving, approve, reject, submit, run, dismiss, task],
  );
};

export default useHitlGateDecision;
